import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faFilePdf } from '@fortawesome/free-solid-svg-icons';

import ElectricalSpecBody, { policzParametry } from './ElectricalSpec';
import ProductDescriptionBody from './ProductDescription';
import ModelCompatibilityList from './compatibility/ModelCompatibilityList';
import { jestPolska } from '../kraj';

// Wspólny pasek zakładek na karcie modelu: parametry elektryczne, opis produktu
// i kompatybilność, plus odnośnik do karty katalogowej po prawej.
//
// DLACZEGO JEDEN PASEK, A NIE OSOBNE KARTY: to wszystko treść pomocnicza pod
// głównym kafelkiem produktu. Jako osobne karty zajmowały trzy pełne wiersze
// ekranu, zanim użytkownik cokolwiek kliknął, i odsuwały w dół to, po co
// naprawdę przyszedł — oferty.
//
// MAKS. JEDNA OTWARTA: stan trzyma NAZWĘ otwartej sekcji, nie dwa niezależne
// booleany. Dzięki temu „tylko jedna naraz" wynika z kształtu stanu i nie da się
// go złamać przypadkiem przy dodaniu trzeciej sekcji.
// Certyfikat NC RfG falownika (wykazy PTPiREE, backend `certyfikaty.py`): na karcie modelu tylko
// przycisk do PDF-u producenta, gdy go mamy. Bez dopisku o wykazie i bez tekstu, gdy PDF-u nie ma
// (prośba właściciela 2026-10-08). Status wykazu 1.3 pokazuje narzędzie zgłoszeń do OSD.
// Wersja krajowa bez przycisku: wykazy PTPiREE i certyfikaty pod polskie wymogi NC RfG.

const ProductPanels = ({ spec, category, paragraphs, compat, certyfikat }) => {
  const { t } = useTranslation('model');
  const [otwarta, setOtwarta] = useState(null);
  const [podsumowanieCompat, setPodsumowanieCompat] = useState(null);

  // useCallback, bo ta funkcja jest zależnością efektu w ModelCompatibilityList —
  // nowa tożsamość przy każdym renderze kazałaby mu ją odpalać w kółko.
  const przyjmijPodsumowanie = useCallback((s) => setPodsumowanieCompat(s), []);

  const liczbaParametrow = policzParametry(spec, category);
  const maParametry = liczbaParametrow > 0;
  const maOpis = Array.isArray(paragraphs) && paragraphs.length > 0;
  const maKompat = !!(compat && compat.modelId != null);
  const pdfCertyfikatu = (jestPolska && certyfikat?.pdf_url) || null;
  if (!maParametry && !maOpis && !maKompat && !pdfCertyfikatu && !spec?.datasheet_url) return null;

  // 'inverter' = pokazujemy magazyny pasujące do falownika, 'battery' odwrotnie.
  const etykietaKompat = compat?.direction === 'inverter'
    ? t('panele.kompat_magazyny')
    : t('panele.kompat_falowniki');

  const przelacz = (klucz) => setOtwarta((v) => (v === klucz ? null : klucz));

  const zakladka = (klucz, etykieta, dodatek) => {
    const aktywna = otwarta === klucz;
    return (
      <button
        type="button"
        onClick={() => przelacz(klucz)}
        aria-expanded={aktywna}
        aria-controls={`panel-${klucz}`}
        className={`flex items-center gap-2 px-5 md:px-7 py-4 text-left transition-colors
                    ${aktywna ? 'bg-gray-50' : 'hover:bg-gray-50/60'}`}
      >
        <span className="text-sm font-bold text-gray-900">{etykieta}</span>
        {dodatek && <span className="text-xs text-gray-400">{dodatek}</span>}
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`text-gray-400 text-xs transition-transform ${aktywna ? 'rotate-180' : ''}`}
        />
      </button>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center border-b border-transparent">
          {maParametry && zakladka(
            'parametry', t('panele.parametry_elektryczne'),
            t('panele.parametry_licznik', { count: liczbaParametrow }),
          )}
          {maOpis && zakladka('opis', t('panele.opis_produktu'))}
          {maKompat && zakladka('kompatybilnosc', etykietaKompat, podsumowanieCompat)}

          {/* Odnośnik do PDF-a stoi OBOK zakładek, nie w środku żadnej z nich:
              <a> wewnątrz <button> to niepoprawny HTML, a klik w link
              przełączałby przy okazji zwijanie sekcji. Adres waliduje backend
              (biała lista schematów — patrz spec/presenter.py). */}
          {(spec?.datasheet_url || pdfCertyfikatu) && (
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2 px-4 md:px-6 py-2.5">
              {pdfCertyfikatu && (
                <a
                  href={certyfikat.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  title={t('panele.certyfikat_tytul', {
                    numer: certyfikat.pdf_numer || '',
                    zrodlo: certyfikat.pdf_zrodlo || t('panele.certyfikat_zrodlo_producent'),
                  })}
                  className="inline-flex items-center gap-2 rounded-xl border border-blue-200
                             bg-white px-3.5 py-2 text-xs font-semibold text-blue-700
                             hover:bg-blue-50 transition-colors"
                >
                  <FontAwesomeIcon icon={faFilePdf} className="text-sm" />
                  {t('panele.certyfikat_nc_rfg')}
                </a>
              )}
              {spec?.datasheet_url && (
                <a
                  href={spec.datasheet_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-2 rounded-xl border border-blue-200
                             bg-white px-3.5 py-2 text-xs font-semibold text-blue-700
                             hover:bg-blue-50 transition-colors"
                >
                  <FontAwesomeIcon icon={faFilePdf} className="text-sm" />
                  {t('panele.karta_katalogowa')}
                </a>
              )}
            </div>
          )}
        </div>

        {maParametry && otwarta === 'parametry' && (
          <div id="panel-parametry" className="border-t border-gray-100 px-5 md:px-7 py-5 md:py-6">
            {/* Parametry elektryczne zostają OTWARTE dla wszystkich — decyzja
                właściciela 2026-08-11, po krótkim okresie za bramką logowania.
                To główna treść karty modelu i powód, dla którego ktoś na nią
                trafia z wyszukiwarki; zasłonięcie jej zamieniało wejście
                z Google w ślepy zaułek. */}
            <ElectricalSpecBody spec={spec} category={category} />
          </div>
        )}

        {/* Opis JEST w DOM także zwinięty (ukryty wysokością, nie odmontowany):
            bot i czytnik ekranu mają go widzieć niezależnie od stanu zwinięcia.
            To był świadomy wybór przy pierwszej wersji sekcji i zostaje. */}
        {maOpis && (
          <div
            id="panel-opis"
            className={otwarta === 'opis'
              ? 'border-t border-gray-100 px-5 md:px-7 py-5 md:py-6'
              : 'h-0 overflow-hidden'}
            aria-hidden={otwarta !== 'opis'}
          >
            <ProductDescriptionBody paragraphs={paragraphs} />
          </div>
        )}

        {/* Kompatybilność też zostaje zamontowana na stałe, ale z INNEGO powodu niż
            opis: komponent sam pobiera dane i sam liczy pozycje, a tę liczbę
            pokazujemy przy etykiecie zakładki. Odmontowanie go przy zwinięciu
            znaczyłoby pobieranie od nowa przy każdym otwarciu i pustą etykietę
            do czasu pierwszego kliknięcia. Zapytanie leci tak czy inaczej —
            wcześniej ta sekcja też była montowana od razu, tylko zwinięta. */}
        {maKompat && (
          <div
            id="panel-kompatybilnosc"
            className={otwarta === 'kompatybilnosc'
              ? 'border-t border-gray-100 px-5 md:px-7 py-5 md:py-6'
              : 'h-0 overflow-hidden'}
            aria-hidden={otwarta !== 'kompatybilnosc'}
          >
            <ModelCompatibilityList
              direction={compat.direction}
              modelId={compat.modelId}
              headless
              onSummary={przyjmijPodsumowanie}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductPanels;
